import { usePage, Link } from "@inertiajs/react";
import ProjectForm from "@/components/projects/ProjectForm";
import ProjectReviews from "@/components/projects/ProjectReviews";
import HackatimeConnect from "@/components/projects/HackatimeConnect";
import PageHeading from "@/components/layout/PageHeading";
import formatTime from "@/utils/formatTime";
import Layout from "@/layouts/layout";
import Panel from "@/components/ui/Panel";
import Badge from "@/components/ui/Badge";
import Icon from "@/components/ui/Icon";
import Avatar from "@/components/ui/Avatar";
import type { Project, ProjectChange } from "@/interfaces/project";
import type { ProjectReview } from "@/interfaces/project_review";
import type { HackatimeProject } from "@/interfaces/hackatime_project";
import type { ShippableEvent } from "@/interfaces/shippable_event";
import type { PublicUser, ReviewerUser } from "@/interfaces/user";
import type { ProjectTag } from "@/interfaces/project_tag";
import type { ProgramConfig } from "@/interfaces/program";
import clockIcon from "@/assets/icons/clock.svg";

interface Props {
  project: Project & {
    user: ReviewerUser;
    reviews: (ProjectReview & { author: PublicUser })[];
  };
  ships: ProjectChange[];
  hackatime_projects: HackatimeProject[];
  shippable_events: ShippableEvent[];
  travel_grant: boolean;
  tags: ProjectTag[];
  program: ProgramConfig;
  [key: string]: unknown;
}

const STATE_TONES = {
  pending: "neutral",
  submitted: "info",
  approved: "success",
  rejected: "danger",
} as const;

export default function ShowProject() {
  const { props } = usePage<Props>();
  const {
    project,
    ships,
    hackatime_projects,
    shippable_events,
    travel_grant,
    tags,
    user,
    program,
  } = props;

  const showUserInfo =
    (user.is_admin || user.is_reviewer || user.is_fulfiller) &&
    project.user?.id !== user.id;

  const tone =
    STATE_TONES[project.aasm_state as keyof typeof STATE_TONES] ?? "neutral";
  const unshipped = project.reported_seconds - (project.total_seconds ?? 0);
  const shippedFor = shippable_events.find((e) => e.id === project.event_id);

  return (
    <Layout>
      <div className="flex flex-col gap-6">
        <PageHeading
          eyebrow="Projects"
          title={project.title ?? "Untitled project"}
        />

        <Panel title="status.txt" tone="alt">
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <Badge tone={tone}>{project.status}</Badge>
              <span className="text-ink-muted flex items-center gap-1.5">
                <Icon src={clockIcon} label="Time logged" className="h-4 w-4" />
                <span className="font-display text-body font-bold">
                  {formatTime(project.reported_seconds)}
                </span>
              </span>
              {project.high_quality && <Badge tone="brand">High quality</Badge>}
              {shippedFor && (
                <Badge tone="info">Shipping for {shippedFor.name}</Badge>
              )}
            </div>

            <div className="text-caption text-ink-muted flex flex-col gap-1">
              {project.real_approved_seconds > 0 && (
                <p>
                  <strong className="text-leaf">
                    {formatTime(project.real_approved_seconds)} approved
                  </strong>
                  {shippedFor && (
                    <>
                      {" - "}
                      <strong className="text-ink">
                        $
                        {(
                          (project.real_approved_seconds / 3600) *
                          (program.budget_per_hour_cents / 100)
                        ).toFixed(2)}
                      </strong>{" "}
                      towards {shippedFor.name}
                    </>
                  )}
                </p>
              )}

              {project.aasm_state === "submitted" && (
                <p>
                  {formatTime(
                    (project.total_seconds ?? 0) -
                      (project.approved_seconds ?? 0),
                  )}{" "}
                  under review
                  {unshipped > 0 &&
                    ` (${formatTime(unshipped)} logged since you shipped)`}
                </p>
              )}

              {project.aasm_state === "approved" &&
                project.reported_seconds > project.approved_seconds && (
                  <p>
                    {formatTime(
                      project.reported_seconds - project.approved_seconds,
                    )}{" "}
                    logged since your last ship - re-ship to get it counted.
                  </p>
                )}
            </div>

            {(project.demo_link || project.repo_link) && (
              <div className="flex flex-wrap items-center gap-4">
                {project.demo_link && (
                  <a
                    className="text-link text-body underline"
                    href={project.demo_link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Demo
                  </a>
                )}
                {project.repo_link && (
                  <a
                    className="text-link text-body underline"
                    href={project.repo_link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Repository
                  </a>
                )}
              </div>
            )}
          </div>
        </Panel>

        <HackatimeConnect compact />

        {showUserInfo && (
          <Panel title="author.txt">
            <div className="flex flex-wrap items-start gap-4">
              <Avatar
                src={project.user!.avatar}
                name={project.user!.username}
                size="lg"
              />
              <div className="text-caption text-ink-muted flex min-w-0 flex-col gap-0.5">
                {props.user.is_admin ? (
                  <Link
                    href={`/admin/users/${project.user!.id}`}
                    className="font-display text-subheading text-link font-bold underline"
                  >
                    {project.user!.username} ({project.user!.id})
                  </Link>
                ) : (
                  <p className="font-display text-subheading text-ink font-bold">
                    {project.user!.username} ({project.user!.id})
                  </p>
                )}
                <p className="wrap-anywhere">{project.user!.email}</p>
                <p>
                  <span className="text-ink font-bold">Hackatime:</span>{" "}
                  {project.user!.hackatime_id ?? "not linked"}
                  {project.user!.hackatime_id && (
                    <>
                      {" ("}
                      <a
                        href={`https://joe.fraud.hackclub.com/profile/${project.user!.hackatime_id}`}
                        className="text-link underline"
                      >
                        Joe
                      </a>
                      {" | "}
                      <a
                        href={`https://billy.3kh0.net/?u=${project.user!.hackatime_id}`}
                        className="text-link underline"
                      >
                        Billy
                      </a>
                      {")"}
                    </>
                  )}
                </p>
                <p>
                  <span className="text-ink font-bold">Slack:</span>{" "}
                  <a
                    href={`https://hackclub.enterprise.slack.com/team/${project.user!.slack_id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-link underline"
                  >
                    {project.user!.slack_id ?? "not linked"}
                  </a>
                </p>
              </div>
            </div>
          </Panel>
        )}

        <ProjectForm
          project={project}
          hackatime_projects={hackatime_projects}
          shippable_events={shippable_events}
          travel_grant={travel_grant}
          tags={tags}
        />

        <ProjectReviews project={project} ships={ships} />
      </div>
    </Layout>
  );
}
